import React, { useState, useEffect, useCallback } from 'react';
import './ManageUGSubjects.css';
import collegeConfig from '../../config/collegeConfig';
import { FaUpload, FaTimes, FaToggleOn, FaToggleOff } from 'react-icons/fa';

const ManageUGSubjects = () => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importCodeOverride, setImportCodeOverride] = useState('');
  const [importError, setImportError] = useState('');
  const [importCodeConflict, setImportCodeConflict] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  const fetchSubjects = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/subjects?includeInactive=true`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setSubjects(data.subjects || []);
    } catch (err) {
      setError('Failed to load UG subjects: ' + err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchSubjects(); }, [fetchSubjects]);

  const toggleActive = async (subject) => {
    const nextIsActive = !subject.isActive;
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/subjects/${subject._id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ isActive: nextIsActive })
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(`"${subject.name}" ${nextIsActive ? 'reactivated' : 'deactivated'}`);
        fetchSubjects();
      }
    } catch (err) {
      setError('Failed to update subject: ' + err.message);
    }
  };

  const handleImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const parsed = JSON.parse(evt.target.result);
        if (parsed.systemType !== 'pmch-elogbook-ug' || !parsed.subject) {
          setImportError('Invalid file: not a PMCH E-Logbook UG subject export');
          setImportPreview(null);
          return;
        }
        setImportPreview(parsed);
        setImportCodeOverride(parsed.subject.code);
        setImportError('');
        setImportCodeConflict(false);
      } catch {
        setImportError('Invalid JSON file');
        setImportPreview(null);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImportSubmit = async () => {
    if (!importPreview) return;
    setImportLoading(true);
    setImportError('');
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/subjects/import`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ exportData: importPreview, overrideCode: importCodeOverride })
      });
      const data = await res.json();
      if (!data.success) {
        setImportError(data.message || 'Import failed');
        if (data.codeConflict) setImportCodeConflict(true);
        return;
      }
      setSuccess(data.message);
      closeImportModal();
      fetchSubjects();
    } catch (err) {
      setImportError('Error connecting to server: ' + err.message);
    } finally {
      setImportLoading(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportPreview(null);
    setImportCodeOverride('');
    setImportError('');
    setImportCodeConflict(false);
  };

  return (
    <div className="mugs-root">
      <div className="mugs-header">
        <h2>Manage UG Subjects</h2>
        <button className="mugs-btn-primary" onClick={() => setShowImportModal(true)}>
          <FaUpload /> Import subject JSON
        </button>
      </div>

      {error && <div className="mugs-alert mugs-alert-error">{error}</div>}
      {success && <div className="mugs-alert mugs-alert-success">{success}</div>}

      {!loading && subjects.length > 0 && (
        <label className="mugs-show-inactive">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          <span>Show inactive subjects</span>
        </label>
      )}

      {loading ? (
        <div className="mugs-empty">Loading subjects...</div>
      ) : subjects.length === 0 ? (
        <div className="mugs-empty">
          No UG subjects yet. Build one via server/docs/UG_COURSE_BUILDER_GUIDE.md and import its JSON here.
        </div>
      ) : (
        <table className="mugs-table">
          <thead>
            <tr>
              <th>Code</th><th>Name</th><th>Topics</th><th>Competencies</th><th>Loggable</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {subjects.filter(s => showInactive || s.isActive).map(s => (
              <tr key={s._id} className={s.isActive ? '' : 'mugs-row-inactive'}>
                <td className="mugs-code">{s.code}</td>
                <td>{s.name}</td>
                <td>{s.topicCount}</td>
                <td>{s.totalCompetencies}</td>
                <td>{s.loggableCompetencies}</td>
                <td>
                  <button
                    className={`mugs-toggle ${s.isActive ? '' : 'mugs-toggle-inactive'}`}
                    onClick={() => toggleActive(s)}
                    title={s.isActive ? 'Deactivate' : 'Reactivate'}
                  >
                    {s.isActive
                      ? <><FaToggleOn className="mugs-toggle-on" /> Active</>
                      : <><FaToggleOff className="mugs-toggle-off" /> Inactive</>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showImportModal && (
        <div className="mugs-modal-overlay" onClick={closeImportModal}>
          <div className="mugs-modal" onClick={e => e.stopPropagation()}>
            <div className="mugs-modal-head">
              <h3>Import UG subject</h3>
              <FaTimes onClick={closeImportModal} />
            </div>
            <p className="mugs-modal-hint">
              Select a UG subject export file (.json), built per server/docs/UG_COURSE_BUILDER_GUIDE.md.
            </p>
            <input type="file" accept=".json" onChange={handleImportFile} className="mugs-file-input" />

            {importError && <div className="mugs-alert mugs-alert-error">{importError}</div>}

            {importPreview && (
              <div className="mugs-import-preview">
                <div><b>Name:</b> {importPreview.subject.name}</div>
                <div><b>Topics:</b> {(importPreview.subject.topics || []).length}</div>
                {importCodeConflict && (
                  <>
                    <label className="mugs-form-label">Subject code (change to resolve conflict)</label>
                    <input
                      className="mugs-form-input"
                      value={importCodeOverride}
                      onChange={e => setImportCodeOverride(e.target.value.toUpperCase())}
                    />
                  </>
                )}
              </div>
            )}

            <div className="mugs-modal-actions">
              <button className="mugs-btn-secondary" onClick={closeImportModal} disabled={importLoading}>Cancel</button>
              <button className="mugs-btn-primary" onClick={handleImportSubmit} disabled={!importPreview || importLoading}>
                {importLoading ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUGSubjects;
